import Text from "common/Typography/Text";
import Switch from "common/Buttons/Switch";
import React from "react";
import { BaseBotData, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType, getPanelShadow } from "common/NewLook/tokens.ts";
import { displayResultMessage, openMessage } from "utils/misc";
import styled, { useTheme } from "styled-components";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import { usePublicBotToggle } from "@shared/features/bots/hooks";

interface PublicBotToggleProps {
  botData: BaseBotData;
}

const PublicBotToggle = ({ botData }: PublicBotToggleProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { isNewLook } = useTheme();
  const { onChange, isLoading, helperTooltipData } = usePublicBotToggle({
    botData,
    displayResultMessage,
    openMessage,
  });

  if (isNewLook) {
    // New look: a settings row in its own panel, like the sections above.
    return (
      <NewLookRow>
        <NewLookLabel>
          {t("labels.discoverable")}
          <HelperTooltip {...helperTooltipData} />
        </NewLookLabel>
        <Switch
          checked={!!botData.is_public}
          onChange={onChange}
          loading={isLoading}
        />
      </NewLookRow>
    );
  }

  return (
    <Wrapper>
      <TextWrapper as="div">
        Discoverable <HelperTooltip {...helperTooltipData} />
      </TextWrapper>
      <Switch
        checked={!!botData.is_public}
        onChange={onChange}
        loading={isLoading}
      />
    </Wrapper>
  );
};

export default PublicBotToggle;

const Wrapper = styled.div`
  border-top: ${(p) => p.theme.borders.light};
  padding-top: 16px;
  display: flex;
  align-items: center;
  padding-bottom: 40px;
  ${(p) =>
    p.theme.isNewLook &&
    `
    justify-content: space-between;
    margin-top: 12px;
    border-top: ${p.theme.borders.lightest};
  `}
`;
const TextWrapper = styled(Text)`
  margin-right: 12px;
  display: flex;
  align-items: center;
  ${(p) =>
    p.theme.isNewLook &&
    `
    gap: 6px;
    color: ${p.theme.colors.titleColor};
    font-weight: ${p.theme.typography.fontWeights.MEDIUM};
  `}
`;
const NewLookRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 52px;
  margin: 16px 0 32px;
  padding: 0 16px;
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  box-shadow: ${(p) => getPanelShadow(p.theme)};
`;
const NewLookLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
